/**
 * MM Anchor Nav Manager - Moon Moon Agency
 *
 * Barre de navigation sticky d'ancres de page.
 * - Les blocks de type "anchor" ciblent une section par INDEX (1-based) dans
 *   l'ordre du DOM (#MainContent > .shopify-section), hors la nav elle-meme.
 *   On ne peut pas resoudre les sections en Liquid -> resolution JS par position.
 * - Highlight du lien actif via IntersectionObserver (section la plus haute visible).
 * - Mobile : bandeau scroll-x, auto-scroll du lien actif au centre.
 * - Mode editeur (data-design-mode) : injecte un badge numerote sur chaque
 *   section pour que le merchant connaisse l'index a renseigner.
 *
 * Cf. .claude/rules/javascript.md (Manager pattern).
 */

/*********************************************************************************************
 * ⚓ MM ANCHOR NAV MANAGER
 */
const MMAnchorNavManager = {
	config: {
		selectors: {
			nav:          '[data-anchor-nav]',
			link:         '[data-anchor-link]',
			mainSections: '#MainContent > .shopify-section',
		},
		classes: {
			active: 'is-active',
			badge:  'mm-anchor-nav__badge',
		},
		observerOptions: {
			rootMargin: '-20% 0px -70% 0px',
			threshold: 0,
		},
	},

	state: {
		observer: null,
	},

	init() {
		const nav = document.querySelector(this.config.selectors.nav);
		if (!nav) return;
		this.nav = nav;

		this._teardown();
		this.sections = this._collectSections();
		this._bindClicks();
		this._initObserver();
		if (nav.hasAttribute('data-design-mode')) this._injectBadges();

		this._bindEditorEvents();
		window.log?.('info', '⚓ [ANCHOR_NAV]', 'Initialized');
	},

	/* Sections de page, hors celle qui contient la nav (sinon index decales) */
	_collectSections() {
		return Array.from(document.querySelectorAll(this.config.selectors.mainSections))
			.filter(section => !section.querySelector(this.config.selectors.nav));
	},

	_resolveTarget(link) {
		const idx = parseInt(link.dataset.targetIndex, 10);
		if (!idx || idx < 1) return null;
		return this.sections[idx - 1] || null;
	},

	_offset() {
		const navH = this.nav.offsetHeight || 0;
		const headerVar = getComputedStyle(document.documentElement).getPropertyValue('--header-height');
		const headerH = parseInt(headerVar, 10) || 90;
		return headerH + navH;
	},

	_bindClicks() {
		this.nav.querySelectorAll(this.config.selectors.link).forEach(link => {
			link.addEventListener('click', (e) => {
				const target = this._resolveTarget(link);
				if (!target) return;
				e.preventDefault();
				const top = target.getBoundingClientRect().top + window.scrollY - this._offset();
				window.scrollTo({ top: Math.max(top, 0), behavior: 'smooth' });
				this._setActive(link);
			});
		});
	},

	_initObserver() {
		if (!('IntersectionObserver' in window)) return;

		this.map = new Map();
		this.nav.querySelectorAll(this.config.selectors.link).forEach(link => {
			const target = this._resolveTarget(link);
			if (target) this.map.set(target, link);
		});
		if (!this.map.size) return;

		this.state.observer = new IntersectionObserver((entries) => {
			const visible = entries
				.filter(entry => entry.isIntersecting)
				.sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
			if (!visible.length) return;
			const link = this.map.get(visible[0].target);
			if (link) this._setActive(link);
		}, this.config.observerOptions);

		this.map.forEach((_link, section) => this.state.observer.observe(section));
	},

	_setActive(activeLink) {
		this.nav.querySelectorAll(this.config.selectors.link).forEach(link => {
			link.classList.toggle(this.config.classes.active, link === activeLink);
		});
		this._scrollNavToLink(activeLink);
	},

	/* Mobile : centre le lien actif dans le bandeau scrollable horizontal */
	_scrollNavToLink(link) {
		if (this.nav.scrollWidth <= this.nav.clientWidth) return;
		const linkRect = link.getBoundingClientRect();
		const navRect = this.nav.getBoundingClientRect();
		const target = this.nav.scrollLeft + linkRect.left - navRect.left - (navRect.width / 2) + (linkRect.width / 2);
		this.nav.scrollTo({ left: target, behavior: 'smooth' });
	},

	/* ----- Editeur : badges d'index sur chaque section ----- */
	_injectBadges() {
		this.sections.forEach((section, i) => {
			if (section.querySelector('.' + this.config.classes.badge)) return;
			if (getComputedStyle(section).position === 'static') section.style.position = 'relative';
			const badge = document.createElement('span');
			badge.className = this.config.classes.badge;
			badge.textContent = String(i + 1);
			section.appendChild(badge);
		});
	},

	_clearBadges() {
		document.querySelectorAll('.' + this.config.classes.badge).forEach(b => b.remove());
	},

	_teardown() {
		if (this.state.observer) { this.state.observer.disconnect(); this.state.observer = null; }
		this._clearBadges();
	},

	/* Re-init quand le Customizer recharge / reordonne des sections */
	_bindEditorEvents() {
		if (this._editorBound) return;
		this._editorBound = true;
		['shopify:section:load', 'shopify:section:unload', 'shopify:section:reorder'].forEach(evt => {
			document.addEventListener(evt, () => {
				/* requestAnimationFrame : laisse le DOM se stabiliser apres le reorder */
				requestAnimationFrame(() => this.init());
			});
		});
	},
};

/*********************************************************************************************
 * 🚀 INIT
 */
if (document.readyState === 'loading') {
	document.addEventListener('DOMContentLoaded', () => MMAnchorNavManager.init());
} else {
	MMAnchorNavManager.init();
}

window.MMAnchorNavManager = MMAnchorNavManager;
